import { Add, Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Link, TablePagination, Typography } from '@mui/material'
import { AddressAddEditDialog, GreenText } from 'components'
import { Chip } from 'components/chips'
import { useAuth } from 'context'
import * as controllers from 'controllers'
import { AddressDto, CompanyAddressDto, CompanyDto } from 'dtos'
import { DEBOUNCE_DELAY } from 'localConstants'
import { enqueueSnackbar } from 'notistack'
import { CompanyAddressesParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { useDebounce } from 'utils'

interface CompanyAddressesListCardProps {
  company: CompanyDto
  handleRefresh: () => void
}

export default function CompanyAddressesListCard({ company, handleRefresh }: CompanyAddressesListCardProps) {
  const { permissions } = useAuth()

  const [address, setAddress] = useState<CompanyAddressDto>(new CompanyAddressDto())
  const [addressAddEditDialogOpen, setAddressAddEditDialogOpen] = useState<boolean>(false)
  const [addresses, setAddresses] = useState<CompanyAddressDto[]>([])
  const [isGettingCompanyAddresses, setIsGettingCompanyAddresses] = useState<boolean>(false)
  const [isSavingAddress, setIsSavingAddress] = useState<boolean>(false)
  const [parameters, setParameters] = useState<CompanyAddressesParameters>({ page: 0, pageSize: 3 })
  const [totalCount, setTotalCount] = useState<number>(0)

  const getCompanyAddresses = useDebounce((companyId: string, parameters: CompanyAddressesParameters) => {
    setIsGettingCompanyAddresses(true)
    controllers
      .getCompanyAddresses(companyId, parameters)
      .then(response => {
        setTotalCount(response.totalCount ?? 0)
        setAddresses(response.value)
      })
      .finally(() => {
        setIsGettingCompanyAddresses(false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    if (company.companyGuid) {
      getCompanyAddresses(company.companyGuid, parameters)
    }
  }, [company, parameters, getCompanyAddresses])

  return (
    <>
      {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
        <AddressAddEditDialog
          initialValues={address}
          isBrokerCompany={company.isBrokerCompany ?? false}
          isRegisteredImporter={company.isRegisteredImporterCompany ?? false}
          loading={isSavingAddress}
          onClose={() => {
            setAddressAddEditDialogOpen(false)
          }}
          onSave={values => {
            setIsSavingAddress(true)

            const requestMethod = values.addressGuid ? controllers.updateCompanyAddress : controllers.createCompanyAddress

            requestMethod(company.companyGuid!, values)
              .then(response => {
                enqueueSnackbar('Address Saved Successfully!', { variant: 'success' })
                // getCompanyAddresses(company.companyGuid!, parameters)
                setAddressAddEditDialogOpen(false)
                handleRefresh()
              })
              .finally(() => {
                setIsSavingAddress(false)
              })
          }}
          open={addressAddEditDialogOpen}
        />
      )}

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={6}>
                <Typography fontWeight='bold' variant='h5'>
                  Addresses
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
                  <Button
                    endIcon={<Add />}
                    onClick={() => {
                      setAddress(new AddressDto())
                      setAddressAddEditDialogOpen(true)
                    }}
                    variant='text'
                  >
                    ADD
                  </Button>
                )}
              </Grid>
            </Grid>

            {isGettingCompanyAddresses && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingCompanyAddresses &&
              addresses.map(address => (
                <Grid item xs={12} key={address.addressGuid}>
                  <Card variant='outlined'>
                    <CardContent>
                      <Grid container spacing={1}>
                        <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
                          <Grid item xs={12} sm={true} container alignItems='center' spacing={0}>
                            <Grid item>
                              <Typography sx={{ mr: 1 }} variant='h6'>
                                {address.addressNickname}
                              </Typography>
                            </Grid>

                            {address.isActive && (
                              <Grid item>
                                <Chip variant='green'>Active</Chip>
                              </Grid>
                            )}

                            {!address.isActive && (
                              <Grid item>
                                <Chip variant='gray'>Inactive</Chip>
                              </Grid>
                            )}

                            {address.isBillingAddress && (
                              <Grid item>
                                <Chip variant='green'>Billing Address</Chip>
                              </Grid>
                            )}

                            {address.isBrokerCompanyAddress && (
                              <Grid item>
                                <Chip variant='green'>Broker Company Address</Chip>
                              </Grid>
                            )}

                            {address.isClusterConversionCompanyAddress && (
                              <Grid item>
                                <Chip variant='green'>Cluster Conversion Company Address</Chip>
                              </Grid>
                            )}

                            {address.isDamageNotificationAddress && (
                              <Grid item>
                                <Chip variant='green'>Damage Notification Address</Chip>
                              </Grid>
                            )}

                            {address.isExporterCompanyAddress && (
                              <Grid item>
                                <Chip variant='green'>Exporter Company Address</Chip>
                              </Grid>
                            )}

                            {address.isImportLocationAddress && (
                              <Grid item>
                                <Chip variant='green'>Import Location</Chip>
                              </Grid>
                            )}

                            {address.isInboundTransporterAddress && (
                              <Grid item>
                                <Chip variant='green'>Inbound Transporter Address</Chip>
                              </Grid>
                            )}

                            {address.isInspectionCompanyAddress && (
                              <Grid item>
                                <Chip variant='green'>Inspection Company Address</Chip>
                              </Grid>
                            )}

                            {address.isInsuranceUnderwriterCompanyAddress && (
                              <Grid item>
                                <Chip variant='green'>Insurance Underwriter Company Address</Chip>
                              </Grid>
                            )}

                            {address.isMichiganSOSBranchAddress && (
                              <Grid item>
                                <Chip variant='green'>Michigan SoS Branch Address</Chip>
                              </Grid>
                            )}

                            {address.isOutboundTransporterAddress && (
                              <Grid item>
                                <Chip variant='green'>Outbound Transporter Address</Chip>
                              </Grid>
                            )}

                            {address.isPhysicalAddress && (
                              <Grid item>
                                <Chip variant='green'>Physical Address</Chip>
                              </Grid>
                            )}

                            {address.isRecallLocationAddress && (
                              <Grid item>
                                <Chip variant='green'>Recall Location</Chip>
                              </Grid>
                            )}

                            {address.isRegisteredImporterAddress && (
                              <Grid item>
                                <Chip variant='green'>Registered Importer Address</Chip>
                              </Grid>
                            )}

                            {address.isShippingAddress && (
                              <Grid item>
                                <Chip variant='green'>Shipping Address</Chip>
                              </Grid>
                            )}

                            {address.isSpecialMailerAddress && (
                              <Grid item>
                                <Chip variant='green'>Special Mailer Address</Chip>
                              </Grid>
                            )}

                            {address.isTitleDestinationAddress && (
                              <Grid item>
                                <Chip variant='green'>Title Destination Address</Chip>
                              </Grid>
                            )}

                            {address.isVehicleDestinationAddress && (
                              <Grid item>
                                <Chip variant='green'>Vehicle Destination Address</Chip>
                              </Grid>
                            )}
                          </Grid>

                          <Grid item xs={12} sm='auto' alignItems='flex-end'>
                            {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
                              <Button
                                endIcon={<Edit />}
                                onClick={() => {
                                  setAddress(address)
                                  setAddressAddEditDialogOpen(true)
                                }}
                                variant='text'
                              >
                                EDIT
                              </Button>
                            )}
                          </Grid>
                        </Grid>

                        <Grid item xs={12}>
                          <Divider />
                        </Grid>

                        {address.phone && (
                          <Grid item xs={12}>
                            <Typography variant='body1'>
                              <Link href={'tel:' + address.phone}>{address.phone}</Link>
                            </Typography>
                          </Grid>
                        )}

                        {address.email && (
                          <Grid item xs={12}>
                            <Typography variant='body1'>
                              <Link href={'mailto:' + address.email}>{address.email}</Link>
                            </Typography>
                          </Grid>
                        )}

                        <Grid item xs={12}>
                          <Typography variant='body1'>{address.address1}</Typography>
                          {address.address2 && <Typography variant='body1'>{address.address2}</Typography>}
                          <Typography variant='body1'>{address.city}</Typography>
                          {address.county && <Typography variant='body1'>{address.county}</Typography>}
                          <Typography variant='body1'>
                            {address.stateProvince} {address.postalCode}
                          </Typography>
                          <Typography variant='body1'>{address.country}</Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}

            <TablePagination
              component='div'
              count={totalCount}
              onPageChange={(_, page: number) => {
                setParameters({
                  ...parameters,
                  page
                })
              }}
              onRowsPerPageChange={e => {
                setParameters({
                  ...parameters,
                  page: 0,
                  pageSize: e.target.value as unknown as number
                })
              }}
              page={parameters.page}
              rowsPerPage={parameters.pageSize}
              labelRowsPerPage='Cards per page:'
              rowsPerPageOptions={[
                { label: '3', value: 3 },
                { label: '5', value: 5 },
                { label: '10', value: 10 },
                { label: '25', value: 25 },
                { label: '50', value: 50 },
                { label: '100', value: 100 }
              ]}
              SelectProps={{ fullWidth: false }}
            />
            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Add Button:** 
                
Clicking the "Add" button will open the Add Address Dialog for creating a new 
address entry.

**Address Sub Card:**

Each entered address is displayed as a separate Address Sub Card.

**Address Nickname:**

The title of each Address Sub Card is displayed as the Address Nickname.

**Chips:**

Each Address Sub Card can display one or more chips, indicating the address 
type(s). Possible chips include:

- Active
- Inactive
- Billing Address
- Broker Company Address
- Exporter Company Address
- Damage Notification Address
- Import Location
- Inbound Transporter Address
- Inspection Company Address
- Insurance Underwriter Company Address
- Michigan SoS Branch Address
- Outbound Transporter Address
- Physical Address
- Recall Location
- Registered Importer Address
- Shipping Address
- Special Mailer Address
- Title Destination Address
- Vehicle Destination Address

**Edit Button:**

Clicking the Edit button on an Address Sub Card will open the Edit Addresses 
Dialog.

**Address Sub Card Address Information:**

Each Address Sub Card lists the entered address details associated with the 
respective address. If no details are entered, the field may be left blank or display 
a placeholder.                
                `}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
